import { ApolloError, ApolloQueryResult, useQuery } from '@apollo/client';
import cloneDeep from 'lodash/cloneDeep';
import defaultTo from 'lodash/defaultTo';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import get from 'lodash/get';
import set from 'lodash/set';
import {
  EndsAtType,
  PatchOperation,
  PatchRequestOperation,
  PromotionInstance,
  UpdateOrderRequest,
  CustomOrderEntitlement,
} from '@atlassian/cssxp-typescommon';
import { SkeletonLoaderWrapper, SkeletonLoader } from '@css-xp/skeleton-loader';
import Form, { ErrorMessage, Field, FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { PricingPlanCycle, PricingPlanType, BillingFrequency } from '@atlassian/cssxp-ccp-types';
import { AddOn, Maybe, Promotion, generatedTypesNew } from '@css-xp/graphtypes';
import {
  EntitlementDetails as Entitlement,
  FetchEntitlementDetailsQuery,
  FetchEntitlementDetailsQueryVariables,
  InvoiceGroup,
  SyntheticPricingPlan,
  TransactionAccount,
  Promotion as PromotionV2,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getAppropriatePromotions } from '@atlassian/temp-helper';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { getBillEstimate, updateEntitlementPlan, updateEntitlementPromotions } from '@atlassian/temp-proxies';
import { OMSMetadata, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { ApprovalEntityInput, ApprovalType, EntityType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ApprovalFormSection, useApprovals } from '@css-xp/apd-process-manager';
import { TaskDetailAction, useTaskList } from '@css-xp/apd-task-manager';
import {
  ErrorComponents,
  InfoPanel,
  NotificationTypes,
  isDiscretionaryDiscountPromotion,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
  withApollo,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import { getChargeElement } from '@css-xp/base/entitlement-utils';
import { SelectPromotionFormFieldV2 } from '@css-xp/base/forms';
import { fetchAvailablePromotionsForEntitlementQuery } from '../../../../entitlement-page/src/queries/fetch-available-promotions-for-entitlement';
import { fetchEntitlementDetailsQuery } from '../../../../entitlement-page/src/queries/fetch-entitlement-details';
import { ActivePromotionSection } from '../../../components/ActivePromotionSection';
import { HeaderIcon } from '../../../components/HeaderIcon';
import { PromotionFormData, getCustomPromotionInstance } from '../../utils/promotion-utils';
import './entitlement-apply-promotion-pane.scss';
import { sendSuccessfulTimedMetric } from '@atlassian/temp-metal';
import {
  ENABLE_ORDER_BILL_ESTIMATION,
  ENABLE_REMOVE_PROMOTION_API,
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_ANNUAL_TERMS,
  ENABLE_PARTNER_PROMOTION,
  ENABLE_OMS_SERVICE_V2,
} from '@atlassian/cssxp-utils-generic';
import {
  isOfferingAtNotice,
  MARKETPLACE_PROMO_CODE,
  getUserTier,
  PARTNER_DISCOUNT,
  constructCustomOrderRequest,
  isAnnualTermedSubscription,
} from '@atlassian/cssxp-utils-entitlement';
import { UFOEventSource } from '@atlassian/temp-observability';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { analytics, AnalyticsTrackAction, AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import { isTrialEntitlement } from '../../../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';
import {
  getIntendedBillingPeriodState,
  getPartnerPromotionQuery,
} from '../../manage-entitlement/src/manage-entitlement-helper';

interface Props {
  entitlement: Entitlement;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountContext: TransactionAccountContextType;
  addOn?: AddOn;
}
type SelectedPromotion = {
  promotionId: string;
  index: string;
};

type Estimate = {
  response?: any;
  error?: any;
  loading?: boolean;
};

type PartnerPromotionType = {
  error?: any;
  loading?: boolean;
  response?: PromotionV2 | null | undefined;
};

const constructRequestData = ({
  entitlement,
  formData,
  formApi,
  activePromotionsToRemove,
  scheduledPromotionsToRemove,
  patchRemovePromotionEnabled,
  isOmsServiceV2Enabled,
  isAnnualTermEnabled,
}: {
  entitlement: Entitlement;
  formData: PromotionFormData;
  formApi: FormApi<PromotionFormData>;
  activePromotionsToRemove: SelectedPromotion[];
  scheduledPromotionsToRemove: SelectedPromotion[];
  patchRemovePromotionEnabled: boolean;
  isOmsServiceV2Enabled: boolean;
  isAnnualTermEnabled: boolean;
}): any => {
  const transformedData: any = {};
  // setting key to any as getFieldState uses the actual keys of PromotionFormData, which does not include array indices
  formApi.getRegisteredFields().forEach((key: any) => {
    // get the value from the field state or the form data
    const value = formApi.getFieldState(key)?.value ?? get(formData, key);
    set(transformedData, key, value);
  });

  const promotions = Object.keys(transformedData).filter((key) => key.trim().includes('promotionId'));

  const promotionsToAdd: any[] = [];
  promotions?.forEach((key: string, index: number) => {
    // Do not add a promotion id (instead add custom promotion id) for any discretionary/custom promotion
    let promotionToAdd;

    if (!isDiscretionaryDiscountPromotion(transformedData, key)) {
      promotionToAdd = {
        promotionInstanceId: null,
        promotionDefinition: {
          promotionCode: null,
          promotionId: null,
          customisedValues: null,
        },
      };

      if (transformedData[key]?.promotion?.type === MARKETPLACE_PROMO_CODE) {
        promotionToAdd.promotionDefinition.promotionCode = transformedData.promoCode;
      } else {
        promotionToAdd.promotionDefinition.promotionId = transformedData[key]?.value;
      }
    } else {
      promotionToAdd = getCustomPromotionInstance(index, formData);
    }

    promotionToAdd && promotionsToAdd.push(promotionToAdd);
  });

  if (formData?.partnerPromotion?.id) {
    promotionsToAdd.push({
      promotionInstanceId: null,
      promotionDefinition: {
        promotionId: formData?.partnerPromotion?.id,
        promotionCode: null,
        customisedValues: null,
      },
    });
  }

  const filteredPromotionsToAdd = promotionsToAdd.filter(
    // Handles the case if discretionary discount was selected but not selected any custom promotion
    (promotionToAdd) =>
      promotionToAdd.promotionDefinition.promotionId ||
      promotionToAdd.promotionDefinition.promotionCode ||
      promotionToAdd.promotionDefinition.customisedValues
  );

  const requestData: UpdateOrderRequest = {
    metadata: constructMetadata(formData),
    entitlementId: entitlement.id,
    addPromotions: filteredPromotionsToAdd,
    isPromotionStackingAllowed: true,
    productId: entitlement.productId ?? '', //@ts-ignore
    chargeQuantities: entitlement?.scheduledChanges?.chargeQuantities || [],
    units: isStatuspage(entitlement?.productId)
      ? (entitlement?.unitCount as string)
      : getUserTier(entitlement?.subscription)?.toString(),
    chargeElement: getChargeElement(entitlement?.productId as string),
    endsAt: isAnnualTermedSubscription(entitlement, isAnnualTermEnabled)
      ? {
          type: EndsAtType.TIMESTAMP,
          value: entitlement?.endTimestamp as number,
        }
      : undefined,
    nextBillingAnchorTimestamp: entitlement?.nextBillingTimestamp ? `${entitlement?.nextBillingTimestamp}` : undefined,
  };

  let operations;
  let removePromotions;
  if (patchRemovePromotionEnabled) {
    const activePromotionOperations: PatchRequestOperation[] = [];
    const scheduledPromotionOperations: PatchRequestOperation[] = [];

    activePromotionsToRemove.forEach((promo: SelectedPromotion) => {
      activePromotionOperations.push({
        op: PatchOperation.REMOVE,
        path: `/subscription/promotionInstances/${promo.index}`,
      });
    });

    scheduledPromotionsToRemove.forEach((promo: SelectedPromotion) => {
      scheduledPromotionOperations.push({
        op: PatchOperation.REMOVE,
        path: `/subscription/scheduledChanges/promotionInstances/${promo.index}`,
      });
    });
    operations = [...activePromotionOperations, ...scheduledPromotionOperations];
    requestData.operations = operations;
  } else {
    removePromotions = [...activePromotionsToRemove, ...scheduledPromotionsToRemove].map(
      (promotion: SelectedPromotion) => {
        return {
          promotionDefinition: {
            promotionId: promotion.promotionId,
          },
        };
      }
    );
    requestData.removePromotions = removePromotions;
  }

  if (isOmsServiceV2Enabled) {
    const entitlementsList: CustomOrderEntitlement[] = [
      {
        entitlementId: entitlement.id,
        productId: entitlement.productId as string,
        addPromotions: filteredPromotionsToAdd,
        removePromotions: removePromotions,
        operations: operations,
      },
    ];
    const constructV2Request = {
      metadata: constructMetadata(formData),
      orderType: OrderItemType.AMENDMENT_ORDER,
      billingFrequency: entitlement?.billingFrequency as string,
      entitlements: entitlementsList,
      transactionAccountId: entitlement?.transactionAccountId as string,
    };
    return constructCustomOrderRequest(constructV2Request);
  }
  return requestData;
};

const constructMetadata = (formData: PromotionFormData) => {
  const metadata: Record<string, string> = {};
  if (formData.handNote) {
    metadata[OMSMetadata.HAND_NOTE] = formData.handNote;
  }
  if (formData.supportTicketLink) {
    metadata[OMSMetadata.SUPPORT_TICKET_NUMBER] = formData.supportTicketLink;
  }
  return metadata;
};

const getDiscreteDiscountPromotions = (formData: PromotionFormData, formApi: FormApi<PromotionFormData>) => {
  const transformedData: any = {};
  formApi.getRegisteredFields().forEach((key: any) => {
    const value = formApi.getFieldState(key)?.value ?? get(formData, key);
    set(transformedData, key, value);
  });
  const promotions: any[] = [];
  let lastDiscountValue: number | undefined = 0;
  const promotionKeys = Object.keys(transformedData).filter((key) => key.trim().includes('promotionId'));
  promotionKeys?.forEach((key: string, index: number) => {
    if (isDiscretionaryDiscountPromotion(transformedData, key)) {
      promotions.push(getCustomPromotionInstance(index, formData));
      lastDiscountValue = formData.customValue && Number(formData.customValue?.[index]);
    }
  });
  return { promotions, lastDiscountValue };
};

const usePromotionDataForAddOn = (id: Maybe<string>) => {
  // To improve: Fetch only required data
  const { loading, error, data } = useQuery<FetchEntitlementDetailsQuery, FetchEntitlementDetailsQueryVariables>(
    fetchEntitlementDetailsQuery,
    {
      variables: { id },
    }
  );
  const { data: availablePromotionData, loading: availablePromotionLoading, error: availablePromotionError } = useQuery<
    generatedTypesNew.FetchAvailablePromotionsForEntitlementQuery,
    generatedTypesNew.FetchAvailablePromotionsForEntitlementQueryVariables
  >(fetchAvailablePromotionsForEntitlementQuery, {
    variables: { id },
  });

  const availablePromotions = availablePromotionData?.entitlementDetails?.availablePromotions;
  const statuspageEntitlement = {
    ...data?.entitlementDetails,
    ...availablePromotionData?.entitlementDetails,
    availablePromotions: cloneDeep(availablePromotions),
  } as Entitlement;

  return {
    data: statuspageEntitlement,
    loading: loading || availablePromotionLoading,
    error: error || availablePromotionError,
  };
};

let startTime: number;
export const EntitlementApplyPromotionPane = withApollo(
  ({ entitlement, transactionAccountContext, refetch, addOn }: Props) => {
    // The entitlement data gets changed in case of an Add-on, hence, storing the initial entitlement number
    const initialEntitlementNumber = entitlement?.entitlementNumber;
    const [isEnabled] = useFeatureToggle();
    const { closeSidebar } = useSidebar();
    const isOmsServiceV2Enabled = isEnabled(ENABLE_OMS_SERVICE_V2);
    const { error: formError, isProcessing, submitForm, complete, data } = useFormSubmit<any, any>({
      submitFormAction: isOmsServiceV2Enabled ? updateEntitlementPromotions : updateEntitlementPlan,
      onComplete: () => {
        calculateAndSendTimeMetric();
        closeSidebar();
        refetch();
      },
    });

    const calculateAndSendTimeMetric = () => {
      const presentTime = new Date().getTime();
      sendSuccessfulTimedMetric(
        'Add Promotions',
        SignalFxFEPerfStore.FE_USER_TASK_ADD_PROMO_TIME,
        presentTime - startTime
      );
    };
    const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
    const [promotionCodeError, setPromotionCodeError] = useState(false);
    const [activeEntitlementPromotionsToRemove, setActiveEntitlementPromotionsToRemove] = useState<SelectedPromotion[]>(
      []
    );
    const [partnerPromotionData, setPartnerPromotionData] = useState<PartnerPromotionType>({});

    GlobalPageLoadExperience.startPageLoad(UFOEventSource.APPLY_PROMOTION_PANE);
    useEffect(() => {
      GlobalPageLoadExperience.success({ force: true });
    }, []);
    const [estimatedBill, setEstimatedBill] = React.useState<Partial<Estimate>>({ loading: false });
    const [scheduledPromotionsToRemove, setScheduledPromotionsToRemove] = useState<SelectedPromotion[]>([]);
    const [shouldDisableApplyButton, setShouldDisableApplyButton] = useState(true);
    // add all the scheduled promotions for removal when removing an active promotion
    useEffect(() => {
      if (activeEntitlementPromotionsToRemove.length) {
        const allScheduledPromotions = defaultTo(
          appropriateScheduledPromotions?.map((promotion: Promotion, index: number) => {
            return { promotionId: promotion.id as string, index: index.toString() };
          }),
          []
        );
        setScheduledPromotionsToRemove(allScheduledPromotions);
      }
    }, [activeEntitlementPromotionsToRemove]);

    const { isDiscountApprovalFlowEnabled } = useApprovalDeskPermissions();
    const isBillEsimationEnabled = isEnabled(ENABLE_ORDER_BILL_ESTIMATION);
    const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
    const isPartnerPromotionEnabled = isEnabled(ENABLE_PARTNER_PROMOTION);

    const patchRemovePromotionEnabled = isEnabled(ENABLE_REMOVE_PROMOTION_API);
    let entitlementData = entitlement;
    const { parentSen } = entitlement;
    const hasParentEntitlement = () => {
      if (parentSen) {
        return true;
      } else {
        return false;
      }
    };
    const addOnEntitlementNumber = addOn?.entitlementNumber;
    let isAddOnPromotionLoading = false;
    let addOnPromotionError: ApolloError | undefined;
    if (addOnEntitlementNumber) {
      // Get the new appropriate entitlement data if Add on promotion is being applied from its parent entitlement page

      const promotionDataForAddOn = usePromotionDataForAddOn(addOnEntitlementNumber as Maybe<string>);
      if (typeof promotionDataForAddOn !== 'undefined') {
        const { data, loading, error } = promotionDataForAddOn;
        isAddOnPromotionLoading = loading;
        addOnPromotionError = error;
        entitlementData = data;
      }
    }

    const onApprovalComplete = React.useCallback(() => {
      const notificationOptions = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('approval-submit-success')
        .withHeader('Approval request submitted')
        .withMessage('You can view your request in the Approval requests section.')
        .withDismissAfter(5000)
        .build();
      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
      closeSidebar();
      refetch?.();
    }, [closeSidebar, refetch, sendSuccessNotification, setNotificationSent]);

    const { hasOpenApprovalRequest, loading: taskListLoading, error: taskListError, data: taskListData } = useTaskList({
      businessKey: entitlement.entitlementNumber,
      approvalType: ApprovalType.ORDER_DISCOUNT,
      skip: !entitlement.entitlementNumber || !isDiscountApprovalFlowEnabled,
    });

    const {
      approvalRequired,
      approvalError,
      approvalList,
      approvalSlackChannel,
      submitApproval,
      approvalProcessing,
      evaluate: evaluateApproval,
      setApprovalRequired,
    } = useApprovals({
      approvalType: ApprovalType.ORDER_DISCOUNT,
      onApprovalComplete,
    });

    const successMessage = () => {
      return (
        <div>
          <p>You can view the changes in the order history tab.</p>
          <Link to={`#order-history`}>View order history</Link>
        </div>
      );
    };

    if (complete && !notificationSent) {
      let notificationOptions;

      if (data?.transitionTime === 'SCHEDULED') {
        notificationOptions = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withId('apply-promotion-scheduled')
          .withHeader('Changes scheduled')
          .withMessage(successMessage())
          .build();

        analytics.sendTrackEvent({
          action: AnalyticsTrackAction.ADD_SCHEDULED,
          actionSubject: AnalyticsTrackSubject.DISCOUNT,
          attributes: {
            entitlementId: entitlement.id,
          },
        });
      } else {
        notificationOptions = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withId('apply-promotion-success')
          .withHeader('Changes applied successfully')
          .build();

        analytics.sendTrackEvent({
          action: AnalyticsTrackAction.APPLIED,
          actionSubject: AnalyticsTrackSubject.DISCOUNT,
          attributes: {
            entitlementId: entitlement.id,
          },
        });
      }

      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
    }

    const availablePromotions = (entitlementData?.availablePromotions ?? []) as Maybe<Promotion[]>;
    const scheduledPromotions = entitlementData?.scheduledChanges?.promotions?.map((promotion, index) => {
      return {
        ...promotion,
        index: index.toString(),
      };
    }) as Maybe<Promotion[]>;
    const hasScheduledPromotions: boolean = (scheduledPromotions ?? []).length > 0;
    const entitlementPromotions =
      entitlementData?.promotions?.map((promotion, index) => {
        return {
          ...promotion,
          index: index.toString(),
        };
      }) ?? ([] as Promotion[]);
    const hasEntitlementPromotions: boolean = entitlementPromotions?.length > 0;
    const billingFrequency = entitlementData?.billingFrequency;
    const appropriateAvailablePromotions = getAppropriatePromotions(availablePromotions, billingFrequency);
    const appropriateScheduledPromotions = getAppropriatePromotions(scheduledPromotions, billingFrequency);
    const appropriateEntitlementPromotions = getAppropriatePromotions(entitlementPromotions, billingFrequency);
    const appliedPromotionTypes = [
      ...(appropriateScheduledPromotions?.map((item: any) => item.type) ?? []),
      ...(appropriateEntitlementPromotions?.map((item: any) => item.type) ?? []),
    ];

    const isMarketPlacePromocodeCurrentPromotionApplied = (appropriateEntitlementPromotions || []).find(
      (item: any) => item.type === 'PROMO_CODE'
    );
    const isMarketPlacePromocodeScheduledPromotionApplied = (appropriateScheduledPromotions || []).find(
      (item: any) => item.type === 'PROMO_CODE'
    );

    const hasActivePartnerPromotion = (appropriateEntitlementPromotions ?? []).some(
      (promotion: PromotionV2) => promotion?.type === PARTNER_DISCOUNT
    );
    const hasScheduledPartnerPromotion = (appropriateScheduledPromotions ?? []).some(
      (promotion: PromotionV2) => promotion?.type === PARTNER_DISCOUNT
    );

    //@ts-ignore
    if (
      isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE) &&
      entitlement?.isApp &&
      !isMarketPlacePromocodeCurrentPromotionApplied &&
      !isMarketPlacePromocodeScheduledPromotionApplied
    ) {
      appropriateAvailablePromotions.push({
        type: MARKETPLACE_PROMO_CODE,
        title: 'Promo code',
        status: 'ACTIVE',
      });
    }
    const [availablePromotionTypes, setAvailablePromotionTypes] = useState<string[]>(appliedPromotionTypes);

    const { data: dataTrans, getTransactionAccount } = transactionAccountContext;

    const transactionAccount = getTransactionAccount(dataTrans) as TransactionAccount;

    const getDefaultBillingGroup = (invoiceGroupId: string) => {
      return availableInvoiceGroups?.filter((invoiceGroup: InvoiceGroup) => {
        return invoiceGroup.id === invoiceGroupId;
      });
    };

    const scheduledChanges = entitlement?.scheduledChanges ?? null;

    const availableInvoiceGroups = (transactionAccount?.availableInvoiceGroups?.nodes ?? []) as InvoiceGroup[];

    const defaultBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId;

    const hasSingleInvoiceGroup = availableInvoiceGroups?.length === 1;

    const scheduledBillingInvoiceGroupId = scheduledChanges?.scheduledBillingInvoiceGroupId;

    const defaultInvoiceGroupPricingPlan = scheduledBillingInvoiceGroupId
      ? getDefaultBillingGroup(scheduledBillingInvoiceGroupId)?.[0]?.shipToPriceEligibility
      : defaultBillingGroupId
      ? getDefaultBillingGroup(defaultBillingGroupId)?.[0]?.shipToPriceEligibility
      : hasSingleInvoiceGroup
      ? availableInvoiceGroups?.[0]?.shipToPriceEligibility
      : undefined;
    let pricingPlan;
    const setDefaultPricingPlanDetails = (availableSyntheticPricingPlans: Maybe<Maybe<SyntheticPricingPlan>[]>) => {
      const availablePricingPlan =
        availableSyntheticPricingPlans?.map((pricingPlan: Maybe<SyntheticPricingPlan>) => pricingPlan?.type) ?? [];

      const txAccountPricingPlan = (transactionAccount?.billToPartyPricingEligibilty ?? '').toUpperCase();

      const invoiceGroupPricingPlan = (defaultInvoiceGroupPricingPlan ?? '')?.toUpperCase();
      if (
        invoiceGroupPricingPlan &&
        availablePricingPlan.includes(invoiceGroupPricingPlan) &&
        invoiceGroupPricingPlan !== PricingPlanType.COMMERCIAL
      ) {
        pricingPlan = invoiceGroupPricingPlan;
      } else if (
        txAccountPricingPlan &&
        availablePricingPlan.includes(txAccountPricingPlan) &&
        txAccountPricingPlan !== PricingPlanType.COMMERCIAL
      ) {
        pricingPlan = txAccountPricingPlan;
      } else {
        pricingPlan = PricingPlanType.COMMERCIAL;
      }
      return pricingPlan;
    };

    const toggleApplyButtonIfDiscountRemoved = (getState: any) => {
      const promotions = get(getState(), 'values', {});
      const promotionId0 = promotions['promotionId-0'];
      if (!promotionId0?.promotion) setShouldDisableApplyButton(true);
    };

    const updateEntitlementPromotionsToRemove = (getState: any) => (
      promotionId: string,
      index: string,
      isRemoved: boolean,
      promotionType = ''
    ) => {
      if (isRemoved) {
        setActiveEntitlementPromotionsToRemove([...activeEntitlementPromotionsToRemove, { promotionId, index }]);
        const promotionsTypeToRemove = availablePromotionTypes.filter((type) => promotionType !== type);
        setAvailablePromotionTypes(promotionsTypeToRemove);
        if (shouldDisableApplyButton) setShouldDisableApplyButton(false);
      } else {
        toggleApplyButtonIfDiscountRemoved(getState);
        const newPromotionsToRemove = activeEntitlementPromotionsToRemove.filter(
          (promo) => promo.promotionId !== promotionId
        );
        setActiveEntitlementPromotionsToRemove(newPromotionsToRemove);
        setAvailablePromotionTypes([...availablePromotionTypes, promotionType]);
      }
    };

    const updateScheduledPromotionsToRemove = (getState: any) => (
      promotionId: string,
      index: string,
      isRemoved: boolean,
      promotionType = ''
    ) => {
      if (isRemoved) {
        setScheduledPromotionsToRemove([...scheduledPromotionsToRemove, { promotionId, index }]);
        const promotionsTypeToRemove = availablePromotionTypes.filter((type) => promotionType !== type);
        setAvailablePromotionTypes(promotionsTypeToRemove);
        if (shouldDisableApplyButton) setShouldDisableApplyButton(false);
      } else {
        toggleApplyButtonIfDiscountRemoved(getState);
        const newPromotionsToRemove = scheduledPromotionsToRemove.filter((promo) => promo.promotionId !== promotionId);
        setScheduledPromotionsToRemove(newPromotionsToRemove);
        setAvailablePromotionTypes([...availablePromotionTypes, promotionType]);
      }
    };

    const handleFormSubmit = async (formData: PromotionFormData, formApi: FormApi<PromotionFormData>) => {
      startTime = new Date().getTime();
      const requestData = constructRequestData({
        entitlement: entitlementData,
        formData,
        formApi,
        activePromotionsToRemove: activeEntitlementPromotionsToRemove,
        scheduledPromotionsToRemove,
        patchRemovePromotionEnabled,
        isOmsServiceV2Enabled,
        isAnnualTermEnabled,
      });
      const { promotions: discreteDiscountPromotions, lastDiscountValue } = getDiscreteDiscountPromotions(
        formData,
        formApi
      );

      let promotionIds: PromotionInstance[];
      let customPromotions: PromotionInstance[];
      // TODO: should remove this check when FF is completely turned on
      if (isOmsServiceV2Enabled) {
        promotionIds =
          requestData?.entitlements?.[0]?.addPromotions?.filter(
            (value: any) => !value.promotionDefinition?.customisedValues
          ) ?? [];

        customPromotions =
          requestData?.entitlements?.[0]?.addPromotions?.filter(
            (value: any) => !!value.promotionDefinition?.customisedValues
          ) ?? [];
      } else {
        promotionIds =
          requestData.addPromotions?.filter((value: any) => !value.promotionDefinition?.customisedValues) ?? [];
        customPromotions =
          requestData.addPromotions?.filter((value: any) => !!value.promotionDefinition?.customisedValues) ?? [];
      }

      const entity: ApprovalEntityInput = {
        entityType: EntityType.ENTITLEMENT,
        entityId: entitlement.id,
        entityNumber: entitlement.entitlementNumber,
        discountPercentage: lastDiscountValue,
        discountValue: defaultTo(estimatedBill.response?.discretionaryDiscountAmount, 0),
      };

      let billEstimateError = null;
      if (customPromotions.length > 0 && !approvalRequired && isBillEsimationEnabled) {
        try {
          setEstimatedBill({ loading: true });
          const response = await getBillEstimate({
            transactionAccountId: defaultTo(entitlement?.transactionAccountId, ''),
            invoiceGroupId: defaultTo(entitlement.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId, ''),
            entitlementId: entitlement.id,
            orderType: OrderItemType.AMENDMENT_ORDER,
            offeringId: entitlement?.offeringId ?? undefined,
            billingFrequency: (entitlement?.billingFrequency as PricingPlanCycle) ?? undefined,
            pricingPlanType:
              (setDefaultPricingPlanDetails(
                entitlement?.offering?.availableSyntheticPricingPlans ?? []
              ) as PricingPlanType) ?? undefined,
            promotionIds: promotionIds,
            customPromotions: customPromotions,
            skipTrial: true,
            endsAt: isAnnualTermedSubscription(entitlement, isAnnualTermEnabled)
              ? { type: EndsAtType.TIMESTAMP, value: entitlement.endTimestamp as number }
              : undefined,
            nextBillingAnchorTimestamp: entitlement?.nextBillingTimestamp
              ? `${entitlement?.nextBillingTimestamp}`
              : undefined,
            units:
              entitlement.billingFrequency === BillingFrequency.ANNUAL
                ? entitlement?.userTier?.toString()
                : entitlement?.unitCount?.toString(),
          });
          entity.discountValue = defaultTo(response?.discretionaryDiscountAmount, 0);
          billEstimateError = null;
          setEstimatedBill({ response, loading: false, error: null });
        } catch (error: any) {
          billEstimateError = `Could not get the bill estimate due to an error "${error?.message}".`;
          setEstimatedBill({ error: billEstimateError, loading: false, response: null });
          return;
        }
      }

      if (approvalRequired) {
        submitApproval({
          approvalType: ApprovalType.ORDER_DISCOUNT,
          businessKey: entitlement.entitlementNumber as string,
          entityInformation: [entity],
          gsacId: formData.supportTicketLink,
          initiatorComments: formData.comments,
          additionalInformation: requestData,
        });
      } else {
        if (
          isDiscountApprovalFlowEnabled &&
          customPromotions.length > 0 &&
          isBillEsimationEnabled &&
          !billEstimateError
        ) {
          const evaluation = await evaluateApproval([entity]);
          if (evaluation && !evaluation.isApprovalRequired && !evaluation.error) {
            submitForm(requestData);
          }
        } else {
          submitForm(requestData);
        }
      }
    };

    const handleAddPromotion = (item: any) => {
      if (item.promotion.type === 'DISCRETIONARY_DISCOUNT') {
        setShouldDisableApplyButton(true);
      } else {
        setShouldDisableApplyButton(false);
      }
    };

    const hasActiveEntitlementPromotionsToRemove = activeEntitlementPromotionsToRemove.length > 0;
    const addOnName = addOn?.name;
    const isStatuspageProduct = isStatuspage(entitlement?.productId);
    const formHeaderTitle = addOnEntitlementNumber
      ? isStatuspageProduct
        ? 'Add discounts'
        : 'App discounts'
      : 'Add discounts';

    const isAppAtNoticeWarningShown = React.useMemo(() => {
      return entitlement?.isApp && isOfferingAtNotice(entitlement);
    }, [entitlement]);

    const isCurrentEntitlementOnTrial = isTrialEntitlement(entitlement, isStatuspageProduct);

    const setPartnerPromotion = async () => {
      if (isPartnerPromotionEnabled) {
        const intendedBillingPeriodState = getIntendedBillingPeriodState(!isCurrentEntitlementOnTrial);
        try {
          setPartnerPromotionData({
            error: null,
            loading: true,
          });
          const partnerPromotionResponse = await getPartnerPromotionQuery({
            entitlementNumber: entitlement.entitlementNumber,
            intendedBillingPeriodState,
            discountTier: transactionAccount?.partnerDiscountTier,
            programLevelGlobal: transactionAccount?.programLevelGlobal,
          });
          const partnerPromotion = partnerPromotionResponse?.data?.entitlementDetails?.availablePartnerPromotions?.[0];
          setPartnerPromotionData((partnerPromotionData) => ({
            ...partnerPromotionData,
            response: partnerPromotion ?? null,
          }));
          if (partnerPromotion?.type) {
            setAvailablePromotionTypes((availablePromotionTypes) => [
              ...availablePromotionTypes,
              partnerPromotion.type as string,
            ]);
          }
        } catch (error) {
          // TODO: handle error case
          setPartnerPromotionData((partnerPromotionData) => ({
            ...partnerPromotionData,
            error,
          }));
        } finally {
          setPartnerPromotionData((partnerPromotionData) => ({
            ...partnerPromotionData,
            loading: false,
          }));
        }
      }
    };

    useEffect(() => {
      setPartnerPromotion();
    }, []);

    // do not auto-apply partner promotion if entitlement already has partner promotion
    const partnerPromotion =
      hasActivePartnerPromotion || hasScheduledPartnerPromotion ? null : partnerPromotionData?.response;

    const renderPromotionSections = (getState: any) => {
      return (
        <>
          {hasEntitlementPromotions && (
            <FormSection>
              <ActivePromotionSection
                promotions={appropriateEntitlementPromotions as Maybe<Promotion[]>}
                updatePromotionsToRemove={updateEntitlementPromotionsToRemove(getState)}
              />
            </FormSection>
          )}
          {(hasScheduledPromotions || partnerPromotion) && (
            <>
              {hasActiveEntitlementPromotionsToRemove && (
                <FormSection>
                  <InfoPanel
                    type="warning"
                    message="Removal of any active promotion will also remove all scheduled promotions."
                  />
                </FormSection>
              )}
              <FormSection>
                <ActivePromotionSection
                  promotions={appropriateScheduledPromotions}
                  updatePromotionsToRemove={updateScheduledPromotionsToRemove(getState)}
                  heading="Promotions to be applied to next bill"
                  enableIsRemoved={hasActiveEntitlementPromotionsToRemove}
                  partnerPromotion={partnerPromotion}
                />
              </FormSection>
            </>
          )}
          <FormSection>
            <SelectPromotionFormFieldV2
              availablePromotions={appropriateAvailablePromotions}
              availablePromotionTypes={availablePromotionTypes}
              isDisabled={approvalRequired}
              handlePromotionCodeError={setPromotionCodeError}
              entitlementId={entitlement?.id}
              productId={entitlement?.productId}
              onAfterAddPromotion={handleAddPromotion}
              onCustomValueChange={() => {
                setShouldDisableApplyButton(false);
              }}
            />
          </FormSection>
        </>
      );
    };

    if (taskListLoading || partnerPromotionData?.loading) return <LoadingIndicator />;

    if (hasOpenApprovalRequest || !!taskListError) {
      return (
        <div className="entitlement-apply-promotion-form">
          <Form
            onSubmit={() => {
              //@ts-ignore
            }}
          >
            {({ formProps }) => (
              <form {...formProps}>
                <FormHeader title={formHeaderTitle}></FormHeader>
                <FormSection>
                  {!!taskListError && (
                    <InfoPanel
                      type="error"
                      message={
                        <div className="mocked-info-panel">
                          <ErrorComponents.ErrorMessageOnInfoPanel
                            title="We're unable to apply promotion for the following reason"
                            message={taskListError.message}
                          />
                        </div>
                      }
                    />
                  )}
                </FormSection>
                <FormSection>
                  {hasOpenApprovalRequest && (
                    <InfoPanel
                      type="warning"
                      message={
                        <div className="approval-request-inflight-banner">
                          One or more approval requests are currently in progress. If you perform this action, those
                          approval requests will be canceled automatically.
                          {taskListData?.taskList?.tasks?.length && (
                            <TaskDetailAction
                              task={taskListData.taskList.tasks[0]}
                              text="View approval request details"
                              refetch={refetch}
                              refetchAll={refetch}
                            />
                          )}
                        </div>
                      }
                    />
                  )}
                </FormSection>
              </form>
            )}
          </Form>
        </div>
      );
    }

    return (
      <div className="entitlement-apply-promotion-form">
        <Form onSubmit={handleFormSubmit}>
          {({ formProps, getState }) => (
            <form {...formProps}>
              <FormHeader title={formHeaderTitle}>
                {isAddOnPromotionLoading ? (
                  <SkeletonLoaderWrapper>
                    <SkeletonLoader width="50%" />
                    <br />
                  </SkeletonLoaderWrapper>
                ) : (
                  <>
                    <div className="inline-block">
                      <HeaderIcon {...(entitlementData as Entitlement)} />
                    </div>
                    <span className="ml-4">
                      {hasParentEntitlement() && isStatuspageProduct
                        ? `${entitlementData?.edition}`
                        : `${entitlementData?.productName ?? 'Unnamed product'}`}{' '}
                      {initialEntitlementNumber}
                    </span>
                  </>
                )}
              </FormHeader>
              <FormSection>
                {!!formError && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to apply promotion for the following reason"
                        message={formError}
                      />
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                {!!approvalError && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to apply promotion for the following reason"
                        message={approvalError.message}
                      />
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                {!!estimatedBill.error && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to apply promotion for the following reason"
                        message={estimatedBill.error}
                      />
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                {addOnPromotionError && addOnPromotionError?.message && (
                  <InfoPanel
                    type="warning"
                    title="Be cautious when adding any promotions!"
                    message={
                      <>
                        We're unable to fetch the promotion details due to the following reason:
                        <br />
                        {addOnPromotionError.message}
                      </>
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                {isAppAtNoticeWarningShown && (
                  <InfoPanel
                    type="warning"
                    title="This offering is in the process of being archived"
                    message="Keep in mind that any changes will only apply until the subscription end date."
                  />
                )}
              </FormSection>
              {addOnName && (
                <FormSection>
                  <Field
                    name="add-on-or-app-name"
                    defaultValue={addOnName}
                    label={isStatuspageProduct ? 'Additional allocation' : 'App name'}
                  >
                    {() => <p>{addOnName}</p>}
                  </Field>
                </FormSection>
              )}
              {addOnEntitlementNumber && (
                <FormSection>
                  <Field
                    name="add-on-or-app-entitlement-number"
                    defaultValue={addOnEntitlementNumber}
                    label={isStatuspageProduct ? 'Additional allocation entitlement number' : 'App entitlement number'}
                  >
                    {() => <p>{addOnEntitlementNumber}</p>}
                  </Field>
                </FormSection>
              )}
              {isAddOnPromotionLoading ? (
                <SkeletonLoaderWrapper className="skeleton-loader-wrapper-apply-promotion">
                  <br />
                  <SkeletonLoader width="20%" />
                  <SkeletonLoader width="100%" />
                  <SkeletonLoader width="100%" />
                  <SkeletonLoader width="30%" />
                </SkeletonLoaderWrapper>
              ) : (
                renderPromotionSections(getState)
              )}
              <FormSection>
                <Field
                  name="supportTicketLink"
                  label={'Support ticket number'}
                  isRequired={true}
                  validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield
                        {...fieldProps}
                        className="support-ticket"
                        name="supportTicketLink"
                        placeholder={'Add GSAC ticket number'}
                        testId="supportTicketLink-field"
                      />
                      {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                    </>
                  )}
                </Field>
              </FormSection>
              <FormSection>
                <Field
                  label={'Internal comment'}
                  name="handNote"
                  isRequired={true}
                  validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield
                        {...fieldProps}
                        name="handNote"
                        testId="hand-note"
                        placeholder={'Add order comments'}
                      />
                      {error && <ErrorMessage>This field is required</ErrorMessage>}
                    </>
                  )}
                </Field>
              </FormSection>
              {approvalRequired ? (
                <ApprovalFormSection
                  approvalType={ApprovalType.ORDER_DISCOUNT}
                  slackChannel={approvalSlackChannel ?? ''}
                  onCancel={() => closeSidebar()}
                  isLoading={approvalProcessing}
                  approvalItems={approvalList.filter((item) => item.approvalRequired)}
                  onEdit={setApprovalRequired}
                />
              ) : (
                <FormFooter align="end">
                  <ButtonGroup>
                    <Button appearance="subtle" onClick={() => closeSidebar()} aria-label="Cancel changes">
                      Cancel
                    </Button>
                    <LoadingButton
                      appearance="primary"
                      type="submit"
                      isLoading={isProcessing || approvalProcessing || estimatedBill.loading}
                      isDisabled={
                        !!approvalError ||
                        !!estimatedBill.error ||
                        (shouldDisableApplyButton && !partnerPromotion) ||
                        promotionCodeError
                      }
                      testId="apply-promotion"
                      aria-label="Apply changes"
                    >
                      Apply
                    </LoadingButton>
                  </ButtonGroup>
                </FormFooter>
              )}
            </form>
          )}
        </Form>
      </div>
    );
  }
);
